/**
 * Media Gallery Component
 * Fullscreen viewer for images and videos with navigation, zoom, and keyboard shortcuts
 */

import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Download, ZoomIn, ZoomOut } from 'lucide-react';

export interface MediaItem {
  id: string;
  url: string;
  fileName: string;
  mimeType: string;
  type: 'image' | 'video';
}

interface MediaGalleryProps {
  items: MediaItem[];
  initialIndex: number;
  onClose: () => void;
}

const MediaGallery = ({ items, initialIndex, onClose }: MediaGalleryProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });

  const currentItem = items[currentIndex];
  const hasMultiple = items.length > 1;

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
      setIsZoomed(false);
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex((i) => i + 1);
      setIsZoomed(false);
    }
  }, [currentIndex, items.length]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToPrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrev, goToNext]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomOrigin({ x, y });
      setIsZoomed(true);
    }
  };

  if (!currentItem) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={currentItem.fileName || 'Преглед на медия'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
        aria-label="Затвори"
      >
        <X className="w-7 h-7" />
      </button>

      {/* Counter */}
      {hasMultiple && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 text-white/80 text-sm font-medium rounded-full z-10">
          {currentIndex + 1} / {items.length}
        </div>
      )}

      {/* Prev button */}
      {hasMultiple && currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToPrev();
          }}
          className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white/80 hover:text-white rounded-full transition-colors z-10"
          aria-label="Предишно"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      {/* Next button */}
      {hasMultiple && currentIndex < items.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white/80 hover:text-white rounded-full transition-colors z-10"
          aria-label="Следващо"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      {/* Content area */}
      <div
        key={currentIndex}
        className="flex items-center justify-center max-w-[90vw] max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {currentItem.type === 'image' ? (
          <div className="overflow-hidden rounded-lg">
            <img
              src={currentItem.url}
              alt={currentItem.fileName}
              className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-300 ease-in-out select-none"
              style={
                isZoomed
                  ? {
                      transform: 'scale(2)',
                      transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                      cursor: 'zoom-out',
                    }
                  : { cursor: 'zoom-in' }
              }
              onClick={handleImageClick}
              draggable={false}
            />
          </div>
        ) : (
          <video
            controls
            autoPlay
            className="max-w-[90vw] max-h-[85vh] rounded-lg bg-black"
          >
            <source src={currentItem.url} type={currentItem.mimeType} />
          </video>
        )}
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-10">
        {/* Filename + zoom hint */}
        <div className="flex items-center gap-2">
          <span className="text-white/60 text-sm truncate max-w-[50vw]">
            {currentItem.fileName}
          </span>
          {currentItem.type === 'image' && (
            <span className="text-white/30 text-xs flex items-center gap-1">
              {isZoomed ? <ZoomOut className="w-3.5 h-3.5" /> : <ZoomIn className="w-3.5 h-3.5" />}
            </span>
          )}
        </div>

        {/* Download button */}
        <a
          href={currentItem.url}
          download={currentItem.fileName}
          className="flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          <Download className="w-4 h-4" />
          <span>Изтегли</span>
        </a>
      </div>
    </div>
  );
};

export default MediaGallery;
